import { useState } from "react";
import { useTranslation } from "react-i18next";
import { ExternalLink } from "lucide-react";
import { useTenant } from "@/contexts/TenantContext";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

interface TermsCheckboxProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  error?: string;
  disabled?: boolean;
  /** Unique ID suffix to prevent duplicate IDs when multiple instances exist */
  id?: string;
}

/**
 * Terms and Conditions checkbox with link or modal.
 * - If termsAndConditionsUrl is set, clicking "Terms and Conditions" opens the URL in a new tab.
 * - If only termsAndConditionsText is set, clicking shows a modal with the text.
 * - If neither is set, the checkbox is not rendered.
 */
export function TermsCheckbox({
  checked,
  onCheckedChange,
  error,
  disabled,
  id = "terms",
}: TermsCheckboxProps) {
  const { t } = useTranslation();
  const { tenant } = useTenant();
  const [modalOpen, setModalOpen] = useState(false);
  const checkboxId = `${id}-checkbox`;
  const errorId = `${id}-error`;

  // Don't render if no terms configured
  if (!tenant?.termsAndConditionsUrl && !tenant?.termsAndConditionsText) {
    return null;
  }

  const hasUrl = !!tenant.termsAndConditionsUrl;
  const hasText = !!tenant.termsAndConditionsText;

  // Render the "Terms and Conditions" link/trigger
  const renderTermsLink = () => {
    if (hasUrl) {
      // External link to URL
      return (
        <a
          href={tenant.termsAndConditionsUrl!}
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary underline hover:text-primary/80 inline-flex items-center gap-1"
          onClick={(e) => e.stopPropagation()}
        >
          {t("checkout.terms.termsAndConditions")}
          <ExternalLink className="h-3 w-3" />
        </a>
      );
    }

    if (hasText) {
      // Modal trigger
      return (
        <DialogTrigger asChild>
          <button
            type="button"
            className="text-primary underline hover:text-primary/80"
            onClick={(e) => e.stopPropagation()}
          >
            {t("checkout.terms.termsAndConditions")}
          </button>
        </DialogTrigger>
      );
    }

    return null;
  };

  const checkboxContent = (
    <div className="space-y-1">
      <div className="flex items-start gap-2">
        <Checkbox
          id={checkboxId}
          checked={checked}
          onCheckedChange={(value) => onCheckedChange(value === true)}
          disabled={disabled}
          aria-invalid={!!error}
          aria-describedby={error ? errorId : undefined}
          className="mt-0.5"
        />
        <label
          htmlFor={checkboxId}
          className="text-sm leading-tight cursor-pointer"
        >
          {t("checkout.terms.agree")} {renderTermsLink()}
        </label>
      </div>
      {error && (
        <p id={errorId} className="text-sm text-destructive ml-6" role="alert">
          {error}
        </p>
      )}
    </div>
  );

  // Wrap in Dialog if using modal
  if (hasText && !hasUrl) {
    return (
      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        {checkboxContent}
        <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{t("checkout.terms.termsAndConditions")}</DialogTitle>
          </DialogHeader>
          <div className="prose prose-sm dark:prose-invert">
            <div className="whitespace-pre-wrap text-sm text-muted-foreground">
              {tenant.termsAndConditionsText}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return checkboxContent;
}

export default TermsCheckbox;
